import { useState, type FormEvent } from "react";
import { discussions as seedDiscussions, poll as seedPoll } from "@/mocks/suye";
import SectionHeading from "./SectionHeading";

interface NewPost {
  id: number;
  name: string;
  avatar: string;
  tone: string;
  text: string;
  time: string;
  replies: string;
  likes: number;
}

const tones = ["bg-primary-500", "bg-accent-600", "bg-secondary-600", "bg-primary-400", "bg-accent-500"];
const formatNum = (n: number) => (n >= 10000 ? (n / 10000).toFixed(1) + "w" : n >= 1000 ? (n / 1000).toFixed(1) + "k" : String(n));

export default function Discussion() {
  const [threads, setThreads] = useState(seedDiscussions);
  const [extraPosts, setExtraPosts] = useState<NewPost[]>([]);
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [input, setInput] = useState("");
  const [posted, setPosted] = useState("");

  // poll
  const [counts, setCounts] = useState(seedPoll.options.map((o) => o.count));
  const [chosen, setChosen] = useState<number | null>(null);
  const total = counts.reduce((a, b) => a + b, 0);

  const toggleLike = (id: string) => {
    const willLike = !liked[id];
    setLiked((prev) => ({ ...prev, [id]: willLike }));
    setThreads((cur) =>
      cur.map((t) => (t.id === id ? { ...t, likes: Math.max(0, t.likes + (willLike ? 1 : -1)) } : t)),
    );
  };

  const choosePoll = (idx: number) => {
    if (chosen !== null) return;
    setChosen(idx);
    setCounts((cur) => cur.map((c, i) => (i === idx ? c + 1 : c)));
  };

  const submitPost = (e: FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    const seed = seedDiscussions.length + extraPosts.length + 1;
    const item: NewPost = {
      id: seed,
      name: "前排瓜友_" + String(seed).padStart(2, "0"),
      avatar: "瓜",
      tone: tones[seed % tones.length],
      text,
      time: "刚刚",
      replies: "0",
      likes: 1,
    };
    setExtraPosts((cur) => [item, ...cur]);
    setInput("");
    setPosted("已替你占好前排，发言已出现在上面");
    window.setTimeout(() => setPosted(""), 2600);
  };

  const allThreads = [...extraPosts, ...threads];

  return (
    <section id="square" className="border-b border-background-200 bg-background-100/60 py-14 md:py-20">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <SectionHeading
          kicker="围观广场"
          title={
            <>
              你的看法，也是<span className="text-primary-600">热搜的一部分</span>
            </>
          }
          desc="前排同好都在聊什么？投个票、抢个前排，把你的一票留在这里。"
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            {allThreads.map((t) => (
              <article key={t.id} className="rounded-2xl border border-background-200 bg-background-50 p-4 md:p-5">
                <div className="flex items-start gap-3">
                  <span
                    className={
                      "flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-heading text-sm font-bold text-background-50 " +
                      t.tone
                    }
                  >
                    {t.avatar}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-foreground-950">{t.name}</span>
                      <span className="text-xs text-foreground-400">{t.time}</span>
                    </div>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{t.text}</p>
                    <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                      <button
                        type="button"
                        onClick={() => toggleLike(t.id)}
                        className={
                          "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 transition-colors " +
                          (liked[t.id]
                            ? "bg-primary-100 text-primary-700"
                            : "hover:bg-background-100 hover:text-foreground-700")
                        }
                      >
                        <i className={liked[t.id] ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                        顶 {formatNum(t.likes)}
                      </button>
                      <span className="inline-flex items-center gap-1.5">
                        <i className="ri-chat-3-line" /> {t.replies} 条回复
                      </span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-2xl border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
                <i className="ri-bar-chart-box-line text-primary-500" />
                今天投个票
              </h3>
              <p className="mt-1.5 text-xs text-foreground-500">{seedPoll.question}</p>
              <div className="mt-4 space-y-2.5">
                {seedPoll.options.map((o, idx) => {
                  const pct = total ? Math.round((counts[idx] / total) * 100) : 0;
                  const isChosen = chosen === idx;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => choosePoll(idx)}
                      disabled={chosen !== null}
                      className={
                        "relative w-full overflow-hidden rounded-xl border px-3.5 py-3 text-left transition-colors " +
                        (isChosen
                          ? "border-primary-300 bg-primary-50"
                          : chosen !== null
                            ? "border-background-200 bg-background-100/60"
                            : "border-background-200 bg-background-100 hover:border-primary-200")
                      }
                    >
                      <span
                        className={
                          "absolute inset-y-0 left-0 transition-all " +
                          (isChosen ? "bg-primary-100" : chosen !== null ? "bg-background-200" : "bg-primary-100/40")
                        }
                        style={{ width: chosen !== null ? pct + "%" : "0%" }}
                      />
                      <span className="relative flex items-center justify-between gap-2">
                        <span className="text-sm text-foreground-800">{o.label}</span>
                        <span className={"text-xs font-semibold " + (isChosen ? "text-primary-700" : "text-foreground-400")}>
                          {chosen !== null ? pct + "%" : ""}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="mt-3 text-xs text-foreground-400">
                {chosen !== null ? "已记录你的选择，谢谢参与 👇" : "点击选项即可投票"}
                {chosen !== null && <span className="text-primary-600"> · 共 {formatNum(total)} 人已投</span>}
              </p>
            </div>

            <form onSubmit={submitPost} className="rounded-2xl border border-background-200 bg-background-50 p-5">
              <label htmlFor="front" className="flex items-center gap-2 text-sm font-bold text-foreground-950">
                <i className="ri-megaphone-line text-accent-600" />
                抢个前排
              </label>
              <textarea
                id="front"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                maxLength={200}
                rows={3}
                placeholder="对这波瓜你有什么想说的？前排放话，文明围观～"
                className="mt-3 w-full resize-none rounded-xl border border-background-200 bg-background-100 p-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
              />
              <div className="mt-3 flex items-center justify-between gap-3">
                <span className="text-xs text-foreground-400">{input.length}/200</span>
                <button
                  type="submit"
                  disabled={!input.trim()}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-foreground-950 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-foreground-800 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  发布发言 <i className="ri-send-plane-line" />
                </button>
              </div>
              {posted && <p className="mt-3 rounded-lg bg-secondary-100 px-3 py-2 text-xs text-secondary-900">{posted}</p>}
            </form>
          </aside>
        </div>
      </div>
    </section>
  );
}